class CustomerVideosSlider {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.swiper = null;
    this.videos = [];
    this.videoTriggers = [];
    this.tagButtons = [];
    this.allSlides = [];
    this.videoObserver = null;
    this.videoStates = new Map(); // Track video states

    this.init();
  }

  init() {
    this.initSwiper();
    this.initVideoControls();
    this.initVideoElements();
    this.initIntersectionObserver();
    this.initTagFiltering();
  }

  initVideoElements() {
    this.videos.forEach((video) => {
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.muted = true;

      // Initialize video state
      this.videoStates.set(video, {
        hasBeenPlayed: false,
        isEnded: false
      });

      const slide = video.closest('.swiper-slide');
      const overlay = slide.querySelector('.customer-videos-slider__overlay');
      const playButton = slide.querySelector('.customer-videos-slider__play-button');

      // Show poster only for unplayed videos
      this.showPosterOnly(slide);

      slide.classList.remove('video-playing');
    });
  }

  // Helper methods for managing overlay display
  showPosterOnly(slide) {
    const overlay = slide.querySelector('.customer-videos-slider__overlay');
    const poster = slide.querySelector('.customer-videos-slider__overlay-poster');
    const playButton = slide.querySelector('.customer-videos-slider__play-button');

    if (overlay) {
      overlay.style.display = 'flex';
    }
    if (poster) {
      poster.style.display = 'block';
    }
    if (playButton) {
      playButton.style.display = 'flex';
    }
  }

  showPlayButtonOnly(slide) {
    const overlay = slide.querySelector('.customer-videos-slider__overlay');
    const poster = slide.querySelector('.customer-videos-slider__overlay-poster');
    const playButton = slide.querySelector('.customer-videos-slider__play-button');

    if (overlay) {
      overlay.style.display = 'flex';
    }
    if (poster) {
      poster.style.display = 'none';
    }
    if (playButton) {
      playButton.style.display = 'flex';
    }
  }

  hideOverlay(slide) {
    const overlay = slide.querySelector('.customer-videos-slider__overlay');
    if (overlay) {
      overlay.style.display = 'none';
    }
  }

  initSwiper() {
    this.swiper = new Swiper(`.section-${this.sectionId} .swiper`, {
      slidesPerView: 1.88,
      spaceBetween: 20,
      breakpoints: {
        450: {
          slidesPerView: 2.5,
          spaceBetween: 20,
        },
        650: {
          slidesPerView: 3,
          spaceBetween: 21,
        },
        1100: {
          slidesPerView: 4,
          spaceBetween: 21,
        }
      },
      pagination: {
        el: `.section-${this.sectionId} .swiper-pagination`,
        clickable: true,
      },
      navigation: {
        nextEl: `.section-${this.sectionId} .swiper-button-next`,
        prevEl: `.section-${this.sectionId} .swiper-button-prev`,
      },
    });
  }

  initVideoControls() {
    this.videos = document.querySelectorAll(`.section-${this.sectionId} video`);
    this.videoTriggers = document.querySelectorAll(
      `.section-${this.sectionId} .customer-videos-slider__overlay, .section-${this.sectionId} video, .section-${this.sectionId} .customer-videos-slider__play-button`
    );

    this.videoTriggers.forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleVideoClick(trigger);
      });
    });

    this.videos.forEach((video) => {
      video.addEventListener('ended', () => {
        this.handleVideoEnded(video);
      });

      video.addEventListener('error', (e) => {
        this.handleVideoError(video, e);
      });

      video.addEventListener('canplay', () => {
        const slide = video.closest('.swiper-slide');
        const errorElement = slide.querySelector('.video-error-message');
        if (errorElement) {
          errorElement.parentNode.removeChild(errorElement);
        }
      });

      video.addEventListener('pause', () => {
        if (!video.ended) {
          // Check if this is a user-initiated pause or automatic pause
          const videoState = this.videoStates.get(video);
          if (videoState && videoState.hasBeenPlayed) {
            this.pauseVideoByUser(video);
          } else {
            this.pauseVideo(video);
          }
        }
      });

      video.addEventListener('play', () => {
        this.pauseAllVideos(video);
      });
    });
  }

  initIntersectionObserver() {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5
    };

    this.videoObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting && !entry.target.paused) {
          this.pauseVideo(entry.target);
        }
      });
    }, observerOptions);

    this.videos.forEach((video) => {
      this.videoObserver.observe(video);
    });
  }

  initTagFiltering() {
    this.tagButtons = document.querySelectorAll(`.section-${this.sectionId} .customer-videos-slider__tag`);
    this.allSlides = document.querySelectorAll(`.section-${this.sectionId} .swiper-slide`);

    this.tagButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const selectedTag = button.dataset.tag;
        this.filterVideosByTag(selectedTag);
      });
    });

    this.applyInitialFilter();
  }

  applyInitialFilter() {
    const activeButton = document.querySelector(`.section-${this.sectionId} .customer-videos-slider__tag.active`);
    if (activeButton) {
      const activeTag = activeButton.dataset.tag;
      this.filterVideosByTag(activeTag);
    } else {
      this.updateMobileMargin(this.allSlides.length);
    }
  }

  pauseAllVideos(currentVideo) {
    this.videos.forEach((video) => {
      if (video !== currentVideo && (!video.paused && !video.ended)) {
        this.pauseVideo(video);
      }
    });
  }

  pauseVideo(video) {
    video.pause();
    const slide = video.closest('.swiper-slide');

    // For automatic pauses (intersection observer), show poster if video hasn't been played yet
    const videoState = this.videoStates.get(video);
    if (videoState && !videoState.hasBeenPlayed) {
      this.showPosterOnly(slide);
    } else {
      this.showPlayButtonOnly(slide);
    }

    slide.classList.remove('video-playing');
  }

  pauseVideoByUser(video) {
    video.pause();
    const slide = video.closest('.swiper-slide');

    // When user manually pauses, show only play button on current frame
    this.showPlayButtonOnly(slide);
    slide.classList.remove('video-playing');
  }

  playVideo(video) {
    const slide = video.closest('.swiper-slide');
    const overlay = slide.querySelector('.customer-videos-slider__overlay');
    const playButton = slide.querySelector('.customer-videos-slider__play-button');

    video.muted = false;

    if (video.readyState < 2) {
      video.addEventListener('canplay', () => {
        this.#playVideoWhenReady(video, slide, overlay, playButton);
      }, { once: true });
      
      video.load();
      return;
    }

    this.#playVideoWhenReady(video, slide, overlay, playButton);
  }

  #playVideoWhenReady(video, slide, overlay, playButton) {
    const playPromise = video.play();

    if (playPromise !== undefined) {
      playPromise.then(() => {
        if (overlay) {
          overlay.style.display = 'none';
        }
        if (playButton) {
          playButton.style.display = 'none';
        }
        slide.classList.add('video-playing');
      }).catch(() => {
        video.muted = true;
        video.play().then(() => {
          if (overlay) {
            overlay.style.display = 'none';
          }
          if (playButton) {
            playButton.style.display = 'none';
          }
          slide.classList.add('video-playing');
          
          this.#showVideoError(slide, 'Video playback failed');
        }).catch(() => {
          this.#showVideoError(slide, 'Video playback failed');
          
          if (overlay) {
            overlay.style.display = 'flex';
          }
          if (playButton) {
            playButton.style.display = 'flex';
          }
          slide.classList.remove('video-playing');
        });
      });
    }
  }

  #showVideoError(slide, message) {
    let errorElement = slide.querySelector('.video-error-message');
    if (!errorElement) {
      errorElement = document.createElement('div');
      errorElement.className = 'video-error-message';
      errorElement.style.cssText = `
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: rgba(0, 0, 0, 0.8);
        color: white;
        padding: 10px 20px;
        border-radius: 5px;
        font-size: 14px;
        z-index: 10;
        text-align: center;
        max-width: 80%;
      `;
      slide.style.position = 'relative';
      slide.appendChild(errorElement);
    }
    errorElement.textContent = message;

    setTimeout(() => {
      if (errorElement && errorElement.parentNode) {
        errorElement.parentNode.removeChild(errorElement);
      }
    }, 3000);
  }

  handleVideoClick(trigger) {
    const slide = trigger.closest('.swiper-slide');
    const video = slide.querySelector('video');

    if (!video) {
      return;
    }

    const errorElement = slide.querySelector('.video-error-message');
    if (errorElement) {
      errorElement.parentNode.removeChild(errorElement);
    }

    if (video.paused || video.ended || video.currentTime === 0) {
      // Mark video as played when user starts it
      const videoState = this.videoStates.get(video);
      if (videoState) {
        videoState.hasBeenPlayed = true;
        videoState.isEnded = false;
      }

      this.pauseAllVideos(video);
      this.playVideo(video);
    } else {
      // User manually paused the video
      this.pauseVideoByUser(video);
    }
  }

  handleVideoEnded(video) {
    video.pause();
    video.currentTime = 0;
    video.muted = true;

    const slide = video.closest('.swiper-slide');
    const videoState = this.videoStates.get(video);

    // Mark video as ended
    if (videoState) {
      videoState.isEnded = true;
    }

    // When video ends, show poster
    this.showPosterOnly(slide);
    slide.classList.remove('video-playing');
  }

  handleVideoError(video, error) {
    const slide = video.closest('.swiper-slide');
    this.#showVideoError(slide, 'Error loading video');

    // On error, show poster
    this.showPosterOnly(slide);
  }

  filterVideosByTag(selectedTag) {
    this.tagButtons.forEach((button) => {
      button.classList.remove('active');
      if (button.dataset.tag === selectedTag) {
        button.classList.add('active');
      }
    });
    
    let visibleSlidesCount = 0;
    this.allSlides.forEach((slide) => {
      const slideTag = slide.dataset.tag;
      if (slideTag === selectedTag) {
        slide.style.display = 'block';
        slide.classList.remove('swiper-slide-hidden');
        visibleSlidesCount++;
      } else {
        slide.style.display = 'none';
        slide.classList.add('swiper-slide-hidden');
      }
    });
    
    this.updateMobileMargin(visibleSlidesCount);
    
    this.videos.forEach((video) => {
      if (!video.paused) {
        // When filtering, treat as automatic pause
        this.pauseVideo(video);
      }
    });
    
    setTimeout(() => {
      this.swiper.update();
      this.swiper.updateSlides();
      this.swiper.updateSlidesClasses();
      this.swiper.updateProgress();
      this.swiper.updateActiveIndex();
      this.swiper.updateSlidesClasses();
    }, 100);
  }

  updateMobileMargin(visibleSlidesCount) {
    const mobileMargin = Math.min(visibleSlidesCount * 20, 200) / 2 + 10;
    
    const section = document.querySelector(`.section-${this.sectionId}`);
    if (section) {
      section.style.setProperty('--mobile-margin', `${mobileMargin}px`);
    }
    
    const prevButton = document.querySelector(`.section-${this.sectionId} .swiper-button-prev`);
    const nextButton = document.querySelector(`.section-${this.sectionId} .swiper-button-next`);
    
    if (prevButton) {
      prevButton.style.setProperty('--mobile-margin', `${mobileMargin}px`);
    }
    if (nextButton) {
      nextButton.style.setProperty('--mobile-margin', `${mobileMargin}px`);
    }
  }

  destroy() {
    if (this.videoObserver) {
      this.videoObserver.disconnect();
    }
    if (this.swiper) {
      this.swiper.destroy();
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const sections = document.querySelectorAll('[class*="customer-videos-slider"]');
  sections.forEach((section) => {
    const sectionId = section.className.match(/section-([a-zA-Z0-9_-]+)/);
    if (sectionId) {
      new CustomerVideosSlider(sectionId[1]);
    }
  });
});

window.CustomerVideosSlider = CustomerVideosSlider;